import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import { authenticatedFetch } from "../../auth/auth";

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
const POLL_MS = 15000;
const READ_LIMIT = 1000;

const relativeTime = (value) => {
  const timestamp = new Date(value || "").getTime();
  if (!Number.isFinite(timestamp)) return "agora";
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "agora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h atrás`;
  const days = Math.floor(hours / 24);
  return `${days} dia${days === 1 ? "" : "s"} atrás`;
};

const iconFor = (type) => {
  if (type === "success") {
    return {
      name: "PlusCircle",
      iconClassName: "text-emerald-600",
      badgeClassName: "bg-emerald-50 border-emerald-100"
    };
  }
  if (type === "error") {
    return {
      name: "AlertCircle",
      iconClassName: "text-red-600",
      badgeClassName: "bg-red-50 border-red-100"
    };
  }
  if (type === "warning") {
    return {
      name: "RefreshCw",
      iconClassName: "text-amber-600",
      badgeClassName: "bg-amber-50 border-amber-100"
    };
  }
  return {
    name: "Bell",
    iconClassName: "text-primary",
    badgeClassName: "bg-orange-50 border-orange-100"
  };
};

const NotificationIndicator = ({ placement = "bottom" }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(() => new Set());
  const [currentUserId, setCurrentUserId] = useState("");
  const [loadedStorage, setLoadedStorage] = useState(false);
  const dropdownRef = useRef(null);

  const storageKey = currentUserId
    ? `dibrownie-notifications-read:${currentUserId}`
    : "";

  const persistRead = (next, key = storageKey) => {
    if (!key) return;
    try {
      const values = [...next].slice(-READ_LIMIT);
      window.localStorage.setItem(key, JSON.stringify(values));
    } catch {
      // Se o navegador bloquear storage, o sino continua funcionando na sessão atual.
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/api/tasks/notifications?limit=80`,
        { cache: "no-store" }
      );
      if (!response.ok) return;

      const payload = await response.json();
      const items = Array.isArray(payload?.notifications)
        ? payload.notifications
        : [];
      const userId = String(payload?.currentUserId || "");

      setCurrentUserId(userId);
      setNotifications(items);

      if (!loadedStorage && userId) {
        const key = `dibrownie-notifications-read:${userId}`;
        let saved = null;

        try {
          saved = JSON.parse(window.localStorage.getItem(key) || "null");

          // Migra automaticamente o estado antigo, quando o sino ainda era
          // identificado como exclusivo de Tarefas. Assim nada que o usuário
          // já marcou como lido reaparece depois desta atualização.
          if (!Array.isArray(saved)) {
            const legacyKey = `dibrownie-task-notifications-read:${userId}`;
            const legacySaved = JSON.parse(window.localStorage.getItem(legacyKey) || "null");
            if (Array.isArray(legacySaved)) {
              saved = legacySaved;
              window.localStorage.setItem(key, JSON.stringify(legacySaved.slice(-READ_LIMIT)));
            }
          }
        } catch {
          saved = null;
        }

        // Na primeira vez que esse usuário abre o sino, evita transformar todo
        // o histórico antigo em notificação nova. Depois disso, apenas IDs novos
        // ficam pendentes até serem marcados como lidos.
        const initialReadIds = Array.isArray(saved)
          ? new Set(saved.map(String))
          : new Set(items.map((item) => String(item.id)));

        setReadIds(initialReadIds);
        if (!Array.isArray(saved)) persistRead(initialReadIds, key);
        setLoadedStorage(true);
      }
    } catch {
      // Mantém a interface disponível mesmo se Tarefas estiver temporariamente indisponível.
    }
  };

  useEffect(() => {
    loadNotifications();
    const timer = window.setInterval(loadNotifications, POLL_MS);
    const refresh = () => loadNotifications();

    window.addEventListener("dibrownie-notifications-refresh", refresh);
    // Compatibilidade com os eventos atuais do módulo de Tarefas.
    window.addEventListener("dibrownie-task-notifications-refresh", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("dibrownie-notifications-refresh", refresh);
      window.removeEventListener("dibrownie-task-notifications-refresh", refresh);
    };
  }, [loadedStorage]);

  useEffect(() => {
    if (!storageKey) return undefined;

    // Mantém o estado sincronizado caso o sistema esteja aberto em outra aba.
    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      try {
        const values = JSON.parse(event.newValue || "[]");
        setReadIds(new Set(Array.isArray(values) ? values.map(String) : []));
      } catch {
        // ignora valor inválido de storage
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [storageKey]);

  const unreadNotifications = useMemo(
    () => notifications.filter((item) => !readIds.has(String(item.id))),
    [notifications, readIds]
  );

  const unreadCount = unreadNotifications.length;

  const markRead = (id) => {
    const normalizedId = String(id);
    setReadIds((current) => {
      const next = new Set(current);
      next.add(normalizedId);
      persistRead(next);
      return next;
    });
  };

  const markAllAsRead = () => {
    setReadIds((current) => {
      const next = new Set(current);
      notifications.forEach((item) => next.add(String(item.id)));
      persistRead(next);
      return next;
    });
  };

  const openNotification = (notification) => {
    markRead(notification.id);
    setIsOpen(false);

    // Cada módulo pode informar para onde a notificação deve levar. As
    // notificações atuais de Tarefas continuam abrindo o quadro normalmente.
    const target = notification?.action || notification?.path || notification?.route || "/tarefas";
    if (typeof target === "string" && target.startsWith("/")) {
      navigate(target);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className={`notification-trigger relative flex min-h-touch min-w-touch items-center justify-center rounded-xl p-2 text-secondary-foreground transition-smooth ${
          isOpen ? "bg-stone-200 shadow-card" : "hover:bg-stone-200/70"
        }`}
        aria-label={`Notificações${unreadCount ? ` - ${unreadCount} não lidas` : ""}`}
        aria-expanded={isOpen}
      >
        <Icon name="Bell" size={18} />
        {unreadCount > 0 && (
          <div className="pulse-notification absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-error px-1 text-xs font-body-medium text-error-foreground shadow-card">
            {unreadCount > 9 ? "9+" : unreadCount}
          </div>
        )}
      </button>

      {isOpen && (
        <div
          className={`fixed left-3 right-3 top-[4.5rem] z-dropdown overflow-hidden rounded-2xl border border-border bg-popover shadow-modal animate-scale-in ${
            placement === "sidebar"
              ? "sm:fixed sm:bottom-3 sm:left-[calc(var(--app-sidebar-width)+0.75rem)] sm:right-auto sm:top-auto sm:mb-0 sm:w-[23rem] sm:max-h-[calc(100dvh-1.5rem)]"
              : `sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:w-[23rem] ${
                  placement === "top"
                    ? "sm:bottom-full sm:mb-2"
                    : "sm:top-full sm:mt-2"
                }`
          }`}
        >
          <div className="border-b border-border bg-gradient-to-b from-orange-50 to-white px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading-semibold text-[15px] text-popover-foreground">
                  Notificações
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Atualizações importantes de todo o sistema.
                </p>
              </div>

              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="rounded-full border border-[#e4d3bf] bg-[#fff6ea] px-3 py-1 text-xs font-body-medium text-[#8a6240] transition-smooth hover:border-[#d89a3d] hover:bg-[#ffefda] hover:text-[#6b4121] hover:shadow-card"
                >
                  Marcar lidas
                </button>
              )}
            </div>
          </div>

          <div className="notification-scroll max-h-[calc(100dvh-14rem)] overflow-y-auto bg-[#fbf7f2] px-3 py-3 sm:max-h-[28rem]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
                <div className="mb-3 rounded-full bg-[#fff0dc] p-3 text-[#b56b18]">
                  <Icon name="Bell" size={24} />
                </div>
                <p className="text-sm font-body-medium text-popover-foreground">
                  Nenhuma notificação ainda
                </p>
                <p className="mt-1 max-w-[250px] text-xs leading-relaxed text-muted-foreground">
                  As atualizações do sistema vão ficar registradas aqui.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((notification) => {
                  const icon = iconFor(notification.type);
                  const isRead = readIds.has(String(notification.id));
                  return (
                    <button
                      type="button"
                      key={notification.id}
                      onClick={() => openNotification(notification)}
                      className={`notification-card group relative w-full overflow-hidden rounded-2xl border p-3 text-left transition-smooth hover:shadow-hover ${
                        isRead
                          ? "border-[#eadfd2] bg-white hover:border-[#dfc097] hover:bg-[#fffaf4]"
                          : "border-[#edd9bd] bg-[#fff8ef] shadow-card hover:border-[#d79a42] hover:bg-[#fff3e2]"
                      }`}
                    >
                      {!isRead && <div className="absolute left-0 top-0 h-full w-1 bg-primary" />}

                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border ${icon.badgeClassName}`}>
                          <Icon
                            name={icon.name}
                            size={18}
                            className={icon.iconClassName}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h4 className={`truncate text-sm font-body-medium ${isRead ? "text-[#6f5b4c]" : "text-popover-foreground"}`}>
                                {notification.title}
                              </h4>
                              {(notification.module || notification.source || notification.taskTitle) && (
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                  {notification.module || notification.source || notification.taskTitle}
                                </p>
                              )}
                            </div>

                            {!isRead && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-body-medium text-primary">
                                Novo
                              </span>
                            )}
                          </div>

                          <p className={`text-sm ${isRead ? "text-[#806d5d]" : "text-foreground"}`}>
                            {notification.message}
                          </p>

                          <div className="mt-2 flex items-center justify-between gap-2">
                            <span className="truncate text-xs text-muted-foreground font-caption">
                              {notification.actorName || "Sistema"} · {relativeTime(notification.createdAt)}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-body-medium text-primary opacity-0 transition-smooth group-hover:opacity-100">
                              Abrir
                              <Icon name="ArrowRight" size={14} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationIndicator;
